import {PredicateEvaluator} from '@mintdata-wsp/common/controllers/spreadsheet/functions/util/eval/PredicateEvaluator';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {ISheetAPI, ISheetRange, SheetType, SimpleNamingContext} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {CoercionUtils} from '@mintdata/mint-kit/impl/coercionUtils/CoercionUtils';
import bind from 'bind-decorator';

export class LookupFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'lookup';
    }

    @Function({
        title: 'find(row, column, sheet)',
        tip: 'Returns the position of a specific character or substring within a text string',
        alias: 'FIND',
        example: 'FIND("M",A2)',
        returns:
            {
                title: 'position',
                tip: 'Returns the position of a specific character or substring within a text string',
                type: Type.Integer
            }
    })
    @bind
    public find(@Param({
                    title: 'textToFind',
                    tip: 'Text to find',
                    type: Type.String
                }) aFindText:AString,
                @Param({
                    title: 'textToFindIn',
                    tip: 'Text to find values in',
                    type: Type.String
                })aWithText:AString,
                @Param({
                    title: 'startNum',
                    tip: 'Starting position to start searching from',
                    type: Type.Integer,
                    optional: true
                }) startNum:AInteger):AInteger | AError
    {
        const findValue = aFindText.value;
        const lookAtValue = aWithText.value;

        let result = null === startNum ? lookAtValue.indexOf(findValue) : lookAtValue.indexOf(findValue, startNum.value);

        if( result === -1 ) return new AError('Not found');

        return new AInteger(result + 1);
    }

    @Function({
        title: 'match(value, range, [match_type])',
        tip: 'Searches for a specified item in a range of cells, and then returns the relative position of that item in the range. For example, if the range A1:A3 contains the values 5, 25, and 38, then the formula =MATCH(25,A1:A3,0) returns the number 2, because 25 is the second item in the range.',
        alias: 'MATCH',
        example: 'MATCH(25,A1:A3,0)',
        returns:
            {
                title: 'position',
                tip: 'Returns the relative position of a specific value from the beginning of the range',
                type: Type.Integer
            }
    })
    @bind
    public match(@Param({
                     title: 'sheetAPI',
                     tip: 'API',
                     type: SheetType.SheetAPI
                 }) api:ISheetAPI,
                 @Param({
                     title: 'lookup_value',
                     tip: 'Value to find',
                     type: SheetType.AnyIValue,
                 }) lookupValue:IValue,
                 @Param({
                     title: 'range',
                     tip: 'Range to find values in',
                     type: Type.Frame
                 })range:IFrame,
                 @Param({
                     title: 'match type',
                     tip: 'Match type. 1 or omitted -  MATCH finds the largest value that is less than or equal to lookup_value. 0 - MATCH finds the first value that is exactly equal to lookup_value. -1 - MATCH finds the smallest value that is greater than or equal to lookup_value',
                     type: Type.Integer,
                     optional: true
                 }) aMatchType:AInteger):AInteger | AError | AEmpty
    {
        let matchType = 1;

        if( aMatchType )
            matchType = aMatchType.value;

        if( !lookupValue && !range )
            return new AError('Invalid arguments!');

        if( matchType !== -1 && matchType !== 0 && matchType !== 1 )
            return new AError('Invalid match type!');

        const index = this._findIndex(range, lookupValue, matchType);
        return index ? new AInteger(index) : AEmpty.Value;
    }

    @bind
    private _findIndex(range:IFrame, lookupValue:IValue, matchType:number):number | null
    {
        let result = null;

        let index = null;
        let found = false;
        let previousValue;

        for( let rowIndex = 0; rowIndex < range.recordCount; rowIndex++ )
        {
            for( let colIndex = 0; colIndex < range.fieldCount; colIndex++ )
            {
                if( found ) return result;

                const slotValue:IValue = range.getCellByOffset(colIndex, rowIndex);

                if( index )
                {
                    index++;
                }
                else
                {
                    index = 1;
                }

                const slotValueToCompare = CoercionUtils.coerce(slotValue, lookupValue.getType(), null);
                if( slotValueToCompare )
                {
                    const comparison = slotValueToCompare.compareTo(lookupValue);

                    if( comparison === 0 )
                    { //0 - values are equal, 1 - val > lookupVal, -1 - val < lookupVal
                        //index = indexExtractor(sheetLocation, range); //found exact value. No need to search further, as it's the final case of all match type
                        result = index;
                        found = true;
                    }
                    else if( comparison === 1 )
                    { //val is greater than value we are looking for
                        if( matchType === -1 )
                        { //we should return the smallest value that is greater than lookup_value.
                            if( !previousValue || slotValue.compareTo(previousValue) === -1 )
                            { //found value that is greater than lookup but smaller than the previous we found. New champion!
                                //index = indexExtractor(sheetLocation, range);
                                result = index;
                                previousValue = slotValue;
                            }
                        }
                    }
                    else if( comparison === -1 )
                    { //val is less than value we are looking for
                        if( matchType === 1 )
                        { //we should return the largest value that is less than lookup_value
                            if( !previousValue || slotValue.compareTo(previousValue) === 1 )
                            { //found value that is smaller than lookup but bigger than the previous we found. New champion!
                                //index = indexExtractor(sheetLocation, range);
                                result = index;
                                previousValue = slotValue;
                            }
                        }
                    }
                }
            }
        }

        return result;
    }

    @Function({
        title: 'vlookup(lookup_value, range, col_index_num, [range_lookup])',
        tip: 'Function to look up a value in a table by matching on the first column',
        alias: 'VLOOKUP',
        example: '=VLOOKUP("value",B1:F23,3,FALSE)',
        returns:
            {
                title: 'Returns',
                tip: 'The matched value from a table.',
                type: SheetType.AnyIValue
            }
    })
    @bind
    public vlookup(@Param({
                       title: 'sheetAPI',
                       tip: 'API',
                       type: SheetType.SheetAPI
                   }) api:ISheetAPI,
                   @Param({
                       title: 'lookup_value',
                       tip: 'Value to find',
                       type: SheetType.AnyIValue,
                   }) lookupValue:IValue,
                   @Param({
                       title: 'range',
                       tip: 'The range of cells in which the VLOOKUP will search for the lookup_value and the return value',
                       type: SheetType.SheetRange
                   })range:ISheetRange,
                   @Param({
                       title: 'col_index_num',
                       tip: 'The column number (starting with 1 for the left-most column of table-array) that contains the return value',
                       type: Type.Integer,
                   }) resultColNum:AInteger,
                   @Param({
                       title: 'range_lookup',
                       tip: 'A logical value that specifies whether you want VLOOKUP to find an approximate or an exact match: TRUE assumes the first column in the table is sorted either numerically or alphabetically, and will then search for the closest value. This is the default method if you don\'t specify one. FALSE searches for the exact value in the first column.',
                       type: Type.Boolean,
                       optional: true
                   }) lookupType:ABoolean):IValue
    {
        if( undefined == resultColNum ) return EvalErrors.Value;
        if( undefined == lookupType ) lookupType = ABoolean.true();

        const values1 = api.getSheetContext().getRangeValue(range);
        const lookupColumn = values1.getColumn(values1.getFieldNames()[0]).rows;
        const resultColumn = values1.getColumn(values1.getFieldNames()[resultColNum.value - 1]).rows;
        return this._lookup(lookupValue, lookupColumn, resultColumn, lookupType.value);
    }

    @Function({
        title: 'HLOOKUP(lookup_value, table_array, row_index_num, [range_lookup])',
        tip: 'Searches for a value in the top row of a table or an array of values, and then returns a value in the same column from a row you specify in the table or array.',
        alias: 'HLOOKUP',
        example: '=HLOOKUP("value",B1:F23,3,FALSE)',
        returns:
            {
                title: 'Returns',
                tip: 'The matched value from a table.',
                type: SheetType.AnyIValue
            }
    })
    @bind
    public hlookup(@Param({
                       title: 'sheetAPI',
                       tip: 'API',
                       type: SheetType.SheetAPI
                   }) api:ISheetAPI,
                   @Param({
                       title: 'lookup_value',
                       tip: 'Value to find',
                       type: SheetType.AnyIValue,
                   }) lookupValue:IValue,
                   @Param({
                       title: 'range',
                       tip: 'The range of cells in which the VLOOKUP will search for the lookup_value and the return value',
                       type: SheetType.SheetRange
                   })range:ISheetRange,
                   @Param({
                       title: 'row_index_num',
                       tip: 'The row number in table_array from which the matching value will be returned.',
                       type: Type.Integer,
                   }) resultRowNum:AInteger,
                   @Param({
                       title: 'range_lookup',
                       tip: 'A logical value that specifies whether you want VLOOKUP to find an approximate or an exact match: TRUE assumes the first column in the table is sorted either numerically or alphabetically, and will then search for the closest value. This is the default method if you don\'t specify one. FALSE searches for the exact value in the first column.',
                       type: Type.Boolean,
                       optional: true
                   }) lookupType:ABoolean):IValue
    {
        if( undefined == resultRowNum ) return EvalErrors.Value;
        if( undefined == lookupType ) lookupType = ABoolean.true();

        const values = api.getSheetContext().getRangeValue(range);
        const lookupRow = values.getRecords()[0];
        const resultRow = values.getRecords()[resultRowNum.value - 1];
        return this._lookup(lookupValue, lookupRow.getCells(), resultRow.getCells(), lookupType.value);
    }

    private _lookup(lookupValue:IValue, lookupArray:IValue[], resultArray:IValue[], lookupType:boolean):IValue
    {
        const exactMatch = (value) =>
        {
            if( Type.isInstanceOf(value, Type.String) )
            {
                return PredicateEvaluator.testPossibleWildcardWithRegexp(value.toString(), lookupValue.toString());
            }
            else
            {
                return value.compareTo(lookupValue) === 0;
            }
        };
        const apprMatch = (value):boolean =>
        {
            return value.compareTo(lookupValue) > 0;
        };
        let index = lookupArray.findIndex(value =>
        {
            return lookupType ? apprMatch(value) : exactMatch(value);
        });
        if( index === -1 ) return EvalErrors.NA;
        //if lookup_type == TRUE we have found the first element that is GREATER than value we are looking for, so we
        //have to return a previous value (because it is less or equal to the one we are interested in)
        //if lookup_type == FALSE we want to find a value we've found as it is exactly what we are looking for
        if( index === 0 ) return resultArray[0];
        return resultArray[lookupType ? (index - 1) : index];
    }
}
